import React from 'react';
import { Box, Container, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import type { LandingCopy, LandingPalette } from './landingCopy';

type IntegrationSectionProps = {
  copy: LandingCopy['integration'];
  palette: LandingPalette;
};

const IntegrationSection: React.FC<IntegrationSectionProps> = ({ copy, palette }) => (
  <Box
    component="section"
    sx={{
      py: { xs: 4, md: 5 },
      bgcolor: palette.pageBg,
      borderTop: `1px solid ${palette.border}`,
      color: palette.text,
    }}
  >
    <Container maxWidth="lg">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gap: 4,
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: '"Outfit", "Cairo", sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              mb: 1.5,
            }}
          >
            {copy.title}
          </Typography>
          <Typography sx={{ color: palette.muted, mb: 2, lineHeight: 1.7, maxWidth: 520 }}>
            {copy.body}
          </Typography>
          <List dense disablePadding>
            {copy.bullets.map((text) => (
              <ListItem key={text} disableGutters sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircleOutline sx={{ color: '#4ADE80', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  primaryTypographyProps={{ variant: 'body2', sx: { color: palette.text } }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box
          sx={{
            p: 4,
            borderRadius: 3,
            border: '1px solid rgba(125,211,252,0.25)',
            background:
              'linear-gradient(145deg, rgba(37,99,235,0.18) 0%, rgba(15,20,25,0.9) 60%)',
            minHeight: 220,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.55)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            System Status
          </Typography>
          <Typography
            sx={{ fontFamily: '"Outfit", "Cairo", sans-serif', fontWeight: 700, fontSize: '1.5rem', mb: 1 }}
          >
            {copy.status}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
            {copy.hint}
          </Typography>
        </Box>
      </Box>
    </Container>
  </Box>
);

export default IntegrationSection;
